import type { LucideIcon } from 'lucide-react';
import {
	Bot,
	Calendar,
	CalendarClock,
	CalendarOff,
	CalendarPlus,
	CalendarX,
	ClipboardList,
	Eye,
	FileText,
	KeyRound,
	LogIn,
	LogOut,
	Mail,
	Mic,
	ShieldAlert,
	ShieldCheck,
	ShieldOff,
	UserMinus,
	UserPen,
	UserPlus,
	Users,
	Video,
	AlertTriangle,
	Sparkles,
	Link2,
	Unlink,
	Play,
	RefreshCw,
} from 'lucide-react';
import {
	PAYMENT_STATUS_TRANSLATION,
	SESSION_STATUS_TRANSLATION,
} from '@/constants/professional/translations';
import { PROFESSIONAL_VISIBILITY_LABELS } from '@/constants/professional/visibility';
import { getProfessionalPermissionTagLabel } from '@/utils/professionalPermissionTags';

export const AUDIT_LOG_CATEGORY_LABEL: Record<
	AuditLogs.AuditLogCategory,
	string
> = {
	AUTHENTICATION: 'Autenticação',
	PROFILE: 'Perfil',
	SCHEDULE: 'Horário',
	SESSION: 'Sessão',
	EXTERNAL_API: 'API externa',
	AUTHORIZATION: 'Autorização',
	NOTIFICATION: 'Notificação',
	PATIENT: 'Paciente',
	MEDICAL_RECORD: 'Prontuário',
	UNAVAILABILITY: 'Indisponibilidade',
};

export const AUDIT_LOG_CATEGORY_DESCRIPTION: Record<
	AuditLogs.AuditLogCategory,
	string
> = {
	AUTHENTICATION: 'Eventos de login e integração com Google OAuth.',
	PROFILE: 'Alterações nos dados do perfil e visibilidade na plataforma.',
	SCHEDULE: 'Criação, edição e exclusão de horários na grade.',
	SESSION: 'Agendamento, status e ciclo de vida das sessões de atendimento.',
	EXTERNAL_API: 'Chamadas a Daily.co e processamento com Claude.',
	AUTHORIZATION:
		'Autorização, moderação e alteração de tags de permissão do profissional.',
	NOTIFICATION: 'E-mails enfileirados para envio (não confirma entrega).',
	PATIENT: 'Criação, edição e exclusão de pacientes na agenda interna.',
	MEDICAL_RECORD:
		'Alterações no prontuário e registros clínicos (sem conteúdo sensível).',
	UNAVAILABILITY:
		'Criação, edição e exclusão de períodos de indisponibilidade.',
};

export const AUDIT_LOG_ACTION_LABEL: Record<AuditLogs.AuditLogAction, string> =
	{
		LOGIN: 'login',
		LOGIN_ATTEMPT: 'tentativa de login',
		LOGOUT: 'logout',
		GOOGLE_OAUTH_AUTHORIZED: 'autorização Google OAuth',
		GOOGLE_OAUTH_REVOKED: 'revogação Google OAuth',
		PROFILE_UPDATED: 'atualização de perfil',
		SCHEDULE_CREATED: 'criação de horário',
		SCHEDULE_UPDATED: 'atualização de horário',
		SCHEDULE_DELETED: 'exclusão de horário',
		SCHEDULE_BULK_DELETED: 'exclusão em massa de horários',
		SESSION_SCHEDULED: 'sessão agendada',
		SESSION_RESCHEDULED: 'sessão reagendada',
		SESSION_STATUS_UPDATED: 'status da sessão alterado',
		SESSION_CANCELLED: 'sessão cancelada',
		SESSION_STARTED: 'sessão iniciada',
		SESSION_FINISHED: 'sessão finalizada',
		DAILY_CO_CALL_STARTED: 'início de chamada Daily.co',
		DAILY_CO_ROOM_CREATED: 'criação de sala Daily.co',
		DAILY_CO_ROOM_RECONCILED: 'reconciliação de sala Daily.co',
		DAILY_CO_TRANSCRIPTION_STARTED: 'início de transcrição Daily.co',
		DAILY_CO_TRANSCRIPTION_FINISHED: 'conclusão de transcrição Daily.co',
		DAILY_CO_TRANSCRIPTION_SKIPPED: 'transcrição Daily.co ignorada',
		DAILY_CO_TRANSCRIPTION_START_FAILED:
			'falha ao iniciar transcrição Daily.co',
		DAILY_CO_FAILED: 'falha Daily.co',
		DAILY_CO_TRANSCRIPTION_FAILED: 'falha de transcrição Daily.co',
		DEEPGRAM_TOKEN_REQUESTED: 'token Deepgram solicitado',
		DEEPGRAM_TOKEN_GRANTED: 'token Deepgram concedido',
		DEEPGRAM_TOKEN_FAILED: 'falha ao conceder token Deepgram',
		DEEPGRAM_TRANSCRIPTION_PROGRESS: 'progresso de transcrição Deepgram',
		DEEPGRAM_TRANSCRIPTION_FINISHED: 'transcrição Deepgram finalizada',
		CLAUDE_API_PROCESSED: 'processamento Claude',
		CLAUDE_API_FAILED: 'falha Claude',
		CLAUDE_API_SKIPPED: 'processamento Claude ignorado',
		PROFESSIONAL_AUTHORIZED: 'autorização do profissional',
		PROFESSIONAL_DEAUTHORIZED: 'desautorização do profissional',
		PROFESSIONAL_PERMISSION_TAGS_UPDATED: 'tags de permissão alteradas',
		PROFESSIONAL_BANNED: 'profissional banido',
		PROFESSIONAL_UNBANNED: 'banimento removido',
		PROFESSIONAL_VISIBILITY_UPDATED: 'visibilidade alterada',
		EMAIL_QUEUED: 'e-mail enfileirado',
		PATIENT_CREATED: 'paciente criado',
		PATIENT_UPDATED: 'paciente atualizado',
		PATIENT_DELETED: 'paciente excluído',
		MEDICAL_RECORD_CREATED: 'prontuário criado',
		MEDICAL_RECORD_UPDATED: 'prontuário atualizado',
		MEDICAL_RECORD_SESSION_CREATED: 'sessão clínica vinculada',
		MEDICAL_RECORD_SESSION_UPDATED: 'sessão clínica atualizada',
		MEDICAL_RECORD_ASSESSMENT_CREATED: 'avaliação criada',
		MEDICAL_RECORD_ASSESSMENT_UPDATED: 'avaliação atualizada',
		MEDICAL_RECORD_ASSESSMENT_DELETED: 'avaliação excluída',
		MEDICAL_RECORD_PLAN_CREATED: 'plano terapêutico criado',
		MEDICAL_RECORD_PLAN_UPDATED: 'plano terapêutico atualizado',
		MEDICAL_RECORD_TASK_CREATED: 'tarefa criada',
		MEDICAL_RECORD_TASK_UPDATED: 'tarefa atualizada',
		UNAVAILABILITY_CREATED: 'indisponibilidade criada',
		UNAVAILABILITY_UPDATED: 'indisponibilidade atualizada',
		UNAVAILABILITY_DELETED: 'indisponibilidade excluída',
	};

export const AUDIT_LOG_ACTION_DESCRIPTION: Record<
	AuditLogs.AuditLogAction,
	string
> = {
	LOGIN: 'Profissional autenticou-se na plataforma.',
	LOGIN_ATTEMPT:
		'Tentativa de login registrada (credenciais inválidas ou conta banida).',
	LOGOUT: 'Profissional encerrou a sessão na plataforma.',
	GOOGLE_OAUTH_AUTHORIZED:
		'Google OAuth autorizado com os escopos concedidos.',
	GOOGLE_OAUTH_REVOKED:
		'Permissões Google revogadas ou invalidadas pela verificação.',
	PROFILE_UPDATED: 'Campos do perfil profissional foram alterados.',
	SCHEDULE_CREATED: 'Novo horário adicionado à grade de disponibilidade.',
	SCHEDULE_UPDATED: 'Horário existente na grade foi modificado.',
	SCHEDULE_DELETED: 'Horário removido da grade de disponibilidade.',
	SCHEDULE_BULK_DELETED:
		'Vários horários de um dia da semana foram removidos de uma vez.',
	SESSION_SCHEDULED: 'Nova sessão de atendimento foi agendada.',
	SESSION_RESCHEDULED: 'Data ou horário da sessão foi alterado.',
	SESSION_STATUS_UPDATED:
		'Status da sessão foi atualizado (cancelamento, confirmação pós-pagamento ou mudança de modalidade).',
	SESSION_CANCELLED:
		'Sessão cancelada com motivo informado (usuário, profissional ou admin), com registro de devolução ou perda do crédito.',
	SESSION_STARTED: 'Sessão de atendimento foi iniciada.',
	SESSION_FINISHED: 'Sessão de atendimento foi finalizada.',
	DAILY_CO_CALL_STARTED:
		'Chamada de vídeo Daily.co iniciada (ainda não emitido pelo backend).',
	DAILY_CO_ROOM_CREATED: 'Sala de vídeo Daily.co criada para a sessão.',
	DAILY_CO_ROOM_RECONCILED:
		'Sala Daily.co reconciliada após alteração de tags de transcrição do profissional.',
	DAILY_CO_TRANSCRIPTION_STARTED:
		'Transcrição da chamada Daily.co foi iniciada.',
	DAILY_CO_TRANSCRIPTION_FINISHED:
		'Transcrição Daily.co concluída com duração registrada.',
	DAILY_CO_TRANSCRIPTION_SKIPPED:
		'Transcrição Daily.co não foi iniciada (informativo).',
	DAILY_CO_TRANSCRIPTION_START_FAILED:
		'Falha ao iniciar transcrição Daily.co na sala.',
	DAILY_CO_FAILED: 'Operação Daily.co falhou.',
	DAILY_CO_TRANSCRIPTION_FAILED:
		'Transcrição Daily.co falhou de forma irrecuperável.',
	DEEPGRAM_TOKEN_REQUESTED:
		'Profissional solicitou token temporário para transcrição Deepgram.',
	DEEPGRAM_TOKEN_GRANTED:
		'Token Deepgram concedido para conexão WebSocket de STT.',
	DEEPGRAM_TOKEN_FAILED: 'Falha ao conceder token Deepgram para a sessão.',
	DEEPGRAM_TRANSCRIPTION_PROGRESS:
		'Snapshot de transcrição Deepgram persistido durante a sessão.',
	DEEPGRAM_TRANSCRIPTION_FINISHED:
		'Transcrição Deepgram finalizada ao encerrar a sessão.',
	CLAUDE_API_PROCESSED:
		'Conteúdo processado pela API Claude com consumo de tokens.',
	CLAUDE_API_FAILED: 'Chamada à API Claude falhou.',
	CLAUDE_API_SKIPPED:
		'Pós-processamento com Claude não foi invocado (informativo).',
	PROFESSIONAL_AUTHORIZED:
		'Administrador autorizou o profissional na plataforma.',
	PROFESSIONAL_DEAUTHORIZED:
		'Administrador removeu a autorização do profissional.',
	PROFESSIONAL_PERMISSION_TAGS_UPDATED:
		'Administrador alterou as tags de permissão do profissional.',
	PROFESSIONAL_BANNED:
		'Administrador ou suporte baniu o profissional na plataforma.',
	PROFESSIONAL_UNBANNED:
		'Administrador ou suporte removeu o banimento do profissional.',
	PROFESSIONAL_VISIBILITY_UPDATED:
		'Visibilidade do profissional na busca e agendamento foi alterada.',
	EMAIL_QUEUED:
		'E-mail colocado na fila de envio (não indica entrega ao destinatário).',
	PATIENT_CREATED: 'Novo paciente adicionado à agenda interna.',
	PATIENT_UPDATED: 'Dados do paciente foram atualizados.',
	PATIENT_DELETED: 'Paciente foi removido da agenda interna.',
	MEDICAL_RECORD_CREATED: 'Prontuário foi criado.',
	MEDICAL_RECORD_UPDATED: 'Prontuário foi atualizado.',
	MEDICAL_RECORD_SESSION_CREATED:
		'Registro de sessão clínica vinculado ao prontuário.',
	MEDICAL_RECORD_SESSION_UPDATED:
		'Registro de sessão clínica do prontuário foi atualizado.',
	MEDICAL_RECORD_ASSESSMENT_CREATED: 'Avaliação clínica foi criada.',
	MEDICAL_RECORD_ASSESSMENT_UPDATED: 'Avaliação clínica foi atualizada.',
	MEDICAL_RECORD_ASSESSMENT_DELETED: 'Avaliação clínica foi excluída.',
	MEDICAL_RECORD_PLAN_CREATED: 'Plano terapêutico foi criado.',
	MEDICAL_RECORD_PLAN_UPDATED: 'Plano terapêutico foi atualizado.',
	MEDICAL_RECORD_TASK_CREATED: 'Tarefa do plano terapêutico foi criada.',
	MEDICAL_RECORD_TASK_UPDATED: 'Tarefa do plano terapêutico foi atualizada.',
	UNAVAILABILITY_CREATED: 'Período de indisponibilidade foi criado.',
	UNAVAILABILITY_UPDATED: 'Período de indisponibilidade foi atualizado.',
	UNAVAILABILITY_DELETED: 'Período de indisponibilidade foi excluído.',
};

export const EMITTED_AUDIT_LOG_ACTIONS: AuditLogs.AuditLogAction[] = [
	'LOGIN',
	'LOGIN_ATTEMPT',
	'LOGOUT',
	'GOOGLE_OAUTH_AUTHORIZED',
	'GOOGLE_OAUTH_REVOKED',
	'PROFILE_UPDATED',
	'SCHEDULE_CREATED',
	'SCHEDULE_UPDATED',
	'SCHEDULE_DELETED',
	'SCHEDULE_BULK_DELETED',
	'SESSION_SCHEDULED',
	'SESSION_RESCHEDULED',
	'SESSION_STATUS_UPDATED',
	'SESSION_CANCELLED',
	'SESSION_STARTED',
	'SESSION_FINISHED',
	'DAILY_CO_ROOM_CREATED',
	'DAILY_CO_ROOM_RECONCILED',
	'DAILY_CO_TRANSCRIPTION_STARTED',
	'DAILY_CO_TRANSCRIPTION_FINISHED',
	'DAILY_CO_TRANSCRIPTION_SKIPPED',
	'DAILY_CO_TRANSCRIPTION_START_FAILED',
	'DAILY_CO_FAILED',
	'DAILY_CO_TRANSCRIPTION_FAILED',
	'CLAUDE_API_PROCESSED',
	'CLAUDE_API_FAILED',
	'CLAUDE_API_SKIPPED',
	'PROFESSIONAL_AUTHORIZED',
	'PROFESSIONAL_DEAUTHORIZED',
	'PROFESSIONAL_PERMISSION_TAGS_UPDATED',
	'PROFESSIONAL_BANNED',
	'PROFESSIONAL_UNBANNED',
	'PROFESSIONAL_VISIBILITY_UPDATED',
	'EMAIL_QUEUED',
	'PATIENT_CREATED',
	'PATIENT_UPDATED',
	'PATIENT_DELETED',
	'MEDICAL_RECORD_CREATED',
	'MEDICAL_RECORD_UPDATED',
	'MEDICAL_RECORD_SESSION_CREATED',
	'MEDICAL_RECORD_SESSION_UPDATED',
	'MEDICAL_RECORD_ASSESSMENT_CREATED',
	'MEDICAL_RECORD_ASSESSMENT_UPDATED',
	'MEDICAL_RECORD_ASSESSMENT_DELETED',
	'MEDICAL_RECORD_PLAN_CREATED',
	'MEDICAL_RECORD_PLAN_UPDATED',
	'MEDICAL_RECORD_TASK_CREATED',
	'MEDICAL_RECORD_TASK_UPDATED',
	'UNAVAILABILITY_CREATED',
	'UNAVAILABILITY_UPDATED',
	'UNAVAILABILITY_DELETED',
];

export const AUDIT_LOG_CATEGORIES: AuditLogs.AuditLogCategory[] = [
	'AUTHENTICATION',
	'PROFILE',
	'SCHEDULE',
	'SESSION',
	'PATIENT',
	'MEDICAL_RECORD',
	'UNAVAILABILITY',
	'EXTERNAL_API',
	'AUTHORIZATION',
	'NOTIFICATION',
];

export const AUDIT_LOG_ACTIONS_BY_CATEGORY: Record<
	AuditLogs.AuditLogCategory,
	AuditLogs.AuditLogAction[]
> = {
	AUTHENTICATION: [
		'LOGIN',
		'LOGIN_ATTEMPT',
		'LOGOUT',
		'GOOGLE_OAUTH_AUTHORIZED',
		'GOOGLE_OAUTH_REVOKED',
	],
	PROFILE: ['PROFILE_UPDATED', 'PROFESSIONAL_VISIBILITY_UPDATED'],
	SCHEDULE: [
		'SCHEDULE_CREATED',
		'SCHEDULE_UPDATED',
		'SCHEDULE_DELETED',
		'SCHEDULE_BULK_DELETED',
	],
	SESSION: [
		'SESSION_SCHEDULED',
		'SESSION_RESCHEDULED',
		'SESSION_STATUS_UPDATED',
		'SESSION_CANCELLED',
		'SESSION_STARTED',
		'SESSION_FINISHED',
	],
	PATIENT: ['PATIENT_CREATED', 'PATIENT_UPDATED', 'PATIENT_DELETED'],
	MEDICAL_RECORD: [
		'MEDICAL_RECORD_CREATED',
		'MEDICAL_RECORD_UPDATED',
		'MEDICAL_RECORD_SESSION_CREATED',
		'MEDICAL_RECORD_SESSION_UPDATED',
		'MEDICAL_RECORD_ASSESSMENT_CREATED',
		'MEDICAL_RECORD_ASSESSMENT_UPDATED',
		'MEDICAL_RECORD_ASSESSMENT_DELETED',
		'MEDICAL_RECORD_PLAN_CREATED',
		'MEDICAL_RECORD_PLAN_UPDATED',
		'MEDICAL_RECORD_TASK_CREATED',
		'MEDICAL_RECORD_TASK_UPDATED',
	],
	UNAVAILABILITY: [
		'UNAVAILABILITY_CREATED',
		'UNAVAILABILITY_UPDATED',
		'UNAVAILABILITY_DELETED',
	],
	EXTERNAL_API: [
		'DAILY_CO_ROOM_CREATED',
		'DAILY_CO_ROOM_RECONCILED',
		'DAILY_CO_TRANSCRIPTION_STARTED',
		'DAILY_CO_TRANSCRIPTION_FINISHED',
		'DAILY_CO_TRANSCRIPTION_SKIPPED',
		'DAILY_CO_TRANSCRIPTION_START_FAILED',
		'DAILY_CO_FAILED',
		'DAILY_CO_TRANSCRIPTION_FAILED',
		'CLAUDE_API_PROCESSED',
		'CLAUDE_API_FAILED',
		'CLAUDE_API_SKIPPED',
	],
	AUTHORIZATION: [
		'PROFESSIONAL_AUTHORIZED',
		'PROFESSIONAL_DEAUTHORIZED',
		'PROFESSIONAL_PERMISSION_TAGS_UPDATED',
		'PROFESSIONAL_BANNED',
		'PROFESSIONAL_UNBANNED',
	],
	NOTIFICATION: ['EMAIL_QUEUED'],
};

export const AUDIT_LOG_CATEGORY_STYLES: Record<
	AuditLogs.AuditLogCategory,
	{ badge: string; dot: string; line: string }
> = {
	AUTHENTICATION: {
		badge: 'bg-sky-100 text-sky-900 ring-sky-200/80',
		dot: 'bg-sky-500 ring-sky-100',
		line: 'bg-sky-200',
	},
	PROFILE: {
		badge: 'bg-violet-100 text-violet-900 ring-violet-200/80',
		dot: 'bg-violet-500 ring-violet-100',
		line: 'bg-violet-200',
	},
	SCHEDULE: {
		badge: 'bg-blue-50 text-blue-800 ring-blue-100',
		dot: 'bg-blue-400 ring-blue-50',
		line: 'bg-blue-100',
	},
	SESSION: {
		badge: 'bg-emerald-100 text-emerald-900 ring-emerald-200/80',
		dot: 'bg-emerald-500 ring-emerald-100',
		line: 'bg-emerald-200',
	},
	EXTERNAL_API: {
		badge: 'bg-slate-100 text-slate-900 ring-slate-200/80',
		dot: 'bg-slate-500 ring-slate-100',
		line: 'bg-slate-200',
	},
	AUTHORIZATION: {
		badge: 'bg-rose-100 text-rose-900 ring-rose-200/80',
		dot: 'bg-rose-500 ring-rose-100',
		line: 'bg-rose-200',
	},
	NOTIFICATION: {
		badge: 'bg-cyan-100 text-cyan-900 ring-cyan-200/80',
		dot: 'bg-cyan-500 ring-cyan-100',
		line: 'bg-cyan-200',
	},
	PATIENT: {
		badge: 'bg-teal-50 text-teal-800 ring-teal-100',
		dot: 'bg-teal-500 ring-teal-50',
		line: 'bg-teal-100',
	},
	MEDICAL_RECORD: {
		badge: 'bg-indigo-50 text-indigo-800 ring-indigo-100',
		dot: 'bg-indigo-500 ring-indigo-50',
		line: 'bg-indigo-100',
	},
	UNAVAILABILITY: {
		badge: 'bg-orange-50 text-orange-800 ring-orange-100',
		dot: 'bg-orange-400 ring-orange-50',
		line: 'bg-orange-100',
	},
};

const LOGIN_ATTEMPT_REASON_LABEL: Record<AuditLogs.LoginAttemptReason, string> =
	{
		invalid_credentials: 'credenciais inválidas',
		account_banned: 'conta banida',
	};

const FILLED_FIELD_COUNT_ACTIONS: AuditLogs.AuditLogAction[] = [
	'PATIENT_CREATED',
	'PATIENT_UPDATED',
	'MEDICAL_RECORD_CREATED',
	'MEDICAL_RECORD_UPDATED',
	'MEDICAL_RECORD_SESSION_CREATED',
	'MEDICAL_RECORD_SESSION_UPDATED',
	'MEDICAL_RECORD_ASSESSMENT_CREATED',
	'MEDICAL_RECORD_ASSESSMENT_UPDATED',
	'MEDICAL_RECORD_PLAN_CREATED',
	'MEDICAL_RECORD_PLAN_UPDATED',
	'MEDICAL_RECORD_TASK_CREATED',
	'MEDICAL_RECORD_TASK_UPDATED',
	'UNAVAILABILITY_CREATED',
	'UNAVAILABILITY_UPDATED',
];

const formatFilledFieldCountSummary = (
	metadata: Record<string, unknown>,
): string | null => {
	const count = readNumber(metadata.filledFieldCount);
	if (count == null) return null;
	return `${count} campo${count === 1 ? '' : 's'}`;
};

const isSessionStatus = (value: string): value is Sessions.SessionStatus =>
	Object.prototype.hasOwnProperty.call(SESSION_STATUS_TRANSLATION, value);

const isPaymentStatus = (value: string): value is Sessions.PaymentStatus =>
	Object.prototype.hasOwnProperty.call(PAYMENT_STATUS_TRANSLATION, value);

const isProfessionalVisibility = (
	value: string,
): value is Professionals.Visibility =>
	value === 'PUBLIC_SEARCH' || value === 'DIRECT_LINK';

const isProfessionalPermissionTag = (
	value: string,
): value is Professionals.PermissionTag =>
	value === 'DEFAULT' ||
	value === 'P2P' ||
	value === 'AI_SANDBOX' ||
	value === 'B2C' ||
	value === 'TRANSCRIPTION' ||
	value === 'TRANSCRIPTION_FOR_DEFAULT_SESSIONS' ||
	value === 'TRANSCRIPTION_MOCK';

const formatAdminIdSummary = (
	metadata: Record<string, unknown>,
): string | null => {
	const adminId = readNumber(metadata.adminId);
	return adminId != null ? `admin #${adminId}` : null;
};

const formatSessionSnapshotSummary = (
	metadata: Record<string, unknown>,
	options?: { includeCallMinutes?: boolean },
): string | null => {
	const parts: string[] = [];
	const status = readString(metadata.status);
	const paymentStatus =
		metadata.paymentStatus === null
			? null
			: readString(metadata.paymentStatus);
	const selectedDate = readString(metadata.selectedDate);

	if (status) {
		parts.push(
			isSessionStatus(status)
				? SESSION_STATUS_TRANSLATION[status]
				: status,
		);
	}
	if (paymentStatus) {
		parts.push(
			isPaymentStatus(paymentStatus)
				? `pag. ${PAYMENT_STATUS_TRANSLATION[paymentStatus]}`
				: `pag. ${paymentStatus}`,
		);
	}
	if (typeof metadata.isOnline === 'boolean') {
		parts.push(metadata.isOnline ? 'online' : 'presencial');
	}
	if (selectedDate) {
		const { date, time } = formatAuditOccurredAtParts(selectedDate);
		parts.push(`${date} ${time}`);
	}
	if (options?.includeCallMinutes) {
		const callMinutes = metadata.callMinutes;
		if (callMinutes === null) {
			parts.push('duração: —');
		} else {
			const minutes = readNumber(callMinutes);
			if (minutes != null) parts.push(`${minutes} min`);
		}
	}

	return parts.length > 0 ? parts.join(' · ') : null;
};

const isLoginAttemptReason = (
	value: string,
): value is AuditLogs.LoginAttemptReason =>
	value === 'invalid_credentials' || value === 'account_banned';

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const readString = (value: unknown): string | undefined =>
	typeof value === 'string' && value.trim() ? value.trim() : undefined;

const readNumber = (value: unknown): number | undefined =>
	typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const readStringArray = (value: unknown): string[] | undefined => {
	if (!Array.isArray(value)) return undefined;
	const items = value.filter(
		(item): item is string => typeof item === 'string',
	);
	return items.length > 0 ? items : undefined;
};

export const formatAuditOccurredAt = (iso: string): string => {
	const { time, date } = formatAuditOccurredAtParts(iso);
	return `${time} (${date})`;
};

export const formatAuditOccurredAtParts = (
	iso: string,
): { time: string; date: string } => {
	const parsed = new Date(iso);
	if (Number.isNaN(parsed.getTime())) {
		return { time: iso, date: '' };
	}

	const hours = String(parsed.getHours()).padStart(2, '0');
	const minutes = String(parsed.getMinutes()).padStart(2, '0');
	const seconds = String(parsed.getSeconds()).padStart(2, '0');
	const day = String(parsed.getDate()).padStart(2, '0');
	const month = String(parsed.getMonth() + 1).padStart(2, '0');
	const year = String(parsed.getFullYear()).slice(-2);

	return {
		time: `${hours}:${minutes}:${seconds}`,
		date: `${day}/${month}/${year}`,
	};
};

const AUDIT_WEEKDAY_LABELS = [
	'domingo',
	'segunda-feira',
	'terça-feira',
	'quarta-feira',
	'quinta-feira',
	'sexta-feira',
	'sábado',
] as const;

const formatAuditRelativeUnit = (
	value: number,
	singular: string,
	plural: string,
	prefix: string,
): string => `${prefix} ${value} ${value === 1 ? singular : plural}`;

export const formatAuditRelativeTime = (
	iso: string,
	now: number = Date.now(),
): string => {
	const parsed = new Date(iso);
	if (Number.isNaN(parsed.getTime())) return iso;

	const diffMs = now - parsed.getTime();
	const prefix = diffMs >= 0 ? 'há' : 'em';
	const absDiffMs = Math.abs(diffMs);

	const seconds = Math.floor(absDiffMs / 1000);
	if (seconds < 45) {
		return seconds <= 1
			? `${prefix} 1 segundo`
			: `${prefix} ${seconds} segundos`;
	}

	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) {
		return formatAuditRelativeUnit(minutes, 'minuto', 'minutos', prefix);
	}

	const hours = Math.floor(minutes / 60);
	if (hours < 24) {
		return formatAuditRelativeUnit(hours, 'hora', 'horas', prefix);
	}

	const days = Math.floor(hours / 24);
	if (days < 7) {
		return formatAuditRelativeUnit(days, 'dia', 'dias', prefix);
	}

	const weeks = Math.floor(days / 7);
	if (weeks < 5) {
		return formatAuditRelativeUnit(weeks, 'semana', 'semanas', prefix);
	}

	const months = Math.floor(days / 30);
	if (months < 12) {
		return formatAuditRelativeUnit(months, 'mês', 'meses', prefix);
	}

	const years = Math.floor(days / 365);
	return formatAuditRelativeUnit(Math.max(years, 1), 'ano', 'anos', prefix);
};

export const formatAuditOccurredAtDetail = (
	iso: string,
): { weekday: string; date: string; time: string } => {
	const parsed = new Date(iso);
	if (Number.isNaN(parsed.getTime())) {
		return { weekday: '', date: iso, time: '' };
	}

	const day = String(parsed.getDate()).padStart(2, '0');
	const month = String(parsed.getMonth() + 1).padStart(2, '0');
	const year = parsed.getFullYear();
	const { time } = formatAuditOccurredAtParts(iso);

	const weekday =
		AUDIT_WEEKDAY_LABELS[parsed.getDay()] ?? AUDIT_WEEKDAY_LABELS[0];

	return {
		weekday: weekday.charAt(0).toUpperCase() + weekday.slice(1),
		date: `${day}/${month}/${year}`,
		time,
	};
};

export const hasAuditMetadata = (metadata: unknown): boolean => {
	if (metadata == null) return false;
	if (isRecord(metadata)) return Object.keys(metadata).length > 0;
	return true;
};

export const formatAuditMetadataSummary = (
	action: AuditLogs.AuditLogAction,
	metadata: unknown,
): string | null => {
	if (!hasAuditMetadata(metadata) || !isRecord(metadata)) return null;

	switch (action) {
		case 'LOGIN_ATTEMPT': {
			const reason = readString(metadata.reason);
			if (!reason) return null;
			return isLoginAttemptReason(reason)
				? LOGIN_ATTEMPT_REASON_LABEL[reason]
				: reason;
		}
		case 'GOOGLE_OAUTH_AUTHORIZED': {
			const scopes = readStringArray(metadata.scopes);
			return scopes ? `escopos: ${scopes.join(', ')}` : null;
		}
		case 'GOOGLE_OAUTH_REVOKED': {
			const reason = readString(metadata.reason);
			return reason ? `motivo: ${reason}` : null;
		}
		case 'PROFILE_UPDATED': {
			const fields = readStringArray(metadata.fields);
			return fields ? `campos: ${fields.join(', ')}` : null;
		}
		case 'SCHEDULE_CREATED':
		case 'SCHEDULE_UPDATED':
		case 'SCHEDULE_DELETED': {
			const weekDay = readString(metadata.weekDay);
			const availableDateId = readString(metadata.availableDateId);
			if (weekDay && availableDateId) {
				return `${weekDay} · ${availableDateId.slice(0, 8)}…`;
			}
			return weekDay ?? availableDateId ?? null;
		}
		case 'SCHEDULE_BULK_DELETED': {
			const weekDay = readString(metadata.weekDay);
			const deletedCount = readNumber(metadata.deletedCount);
			if (weekDay && deletedCount != null) {
				return `${weekDay} · ${deletedCount} horário${
					deletedCount === 1 ? '' : 's'
				}`;
			}
			return weekDay ?? null;
		}
		case 'SESSION_FINISHED':
			return formatSessionSnapshotSummary(metadata, {
				includeCallMinutes: true,
			});
		case 'SESSION_CANCELLED': {
			const cancelledBy = readString(metadata.cancelledBy);
			const reason = readString(metadata.reason);
			const creditRestored = metadata.creditRestored;
			const hours = readNumber(metadata.hoursUntilSession);
			const who =
				cancelledBy === 'USER'
					? 'pelo usuário'
					: cancelledBy === 'PROFESSIONAL'
					  ? 'pelo profissional'
					  : cancelledBy === 'ADMIN'
					    ? 'pelo admin'
					    : null;
			const parts: string[] = [];
			if (who) parts.push(`cancelada ${who}`);
			if (hours != null) parts.push(`${hours.toFixed(1)}h antes`);
			if (typeof creditRestored === 'boolean')
				parts.push(
					creditRestored ? 'crédito devolvido' : 'crédito perdido',
				);
			if (reason) parts.push(`motivo: "${reason}"`);
			return parts.length ? parts.join(' · ') : null;
		}
		case 'SESSION_SCHEDULED':
		case 'SESSION_RESCHEDULED':
		case 'SESSION_STATUS_UPDATED':
		case 'SESSION_STARTED':
			return formatSessionSnapshotSummary(metadata);
		case 'DAILY_CO_ROOM_CREATED':
		case 'DAILY_CO_TRANSCRIPTION_STARTED':
		case 'DAILY_CO_TRANSCRIPTION_SKIPPED':
		case 'DAILY_CO_TRANSCRIPTION_START_FAILED': {
			const roomId = readString(metadata.roomId);
			return roomId ? `sala: ${roomId}` : null;
		}
		case 'DAILY_CO_ROOM_RECONCILED': {
			const roomId = readString(metadata.roomId);
			const status = readString(metadata.status);
			const transcriptionStorageChanged =
				typeof metadata.transcriptionStorageChanged === 'boolean'
					? metadata.transcriptionStorageChanged
					: null;
			const parts: string[] = [];
			if (status) parts.push(`status: ${status}`);
			if (transcriptionStorageChanged != null) {
				parts.push(
					transcriptionStorageChanged
						? 'armazenamento de transcrição alterado'
						: 'armazenamento inalterado',
				);
			}
			if (roomId) parts.push(`sala: ${roomId}`);
			return parts.length > 0 ? parts.join(' · ') : null;
		}
		case 'DAILY_CO_TRANSCRIPTION_FINISHED': {
			const roomId = readString(metadata.roomId);
			const durationSeconds = readNumber(metadata.durationSeconds);
			const parts: string[] = [];
			if (roomId) parts.push(`sala: ${roomId}`);
			if (durationSeconds != null)
				parts.push(`duração: ${durationSeconds}s`);
			return parts.length > 0 ? parts.join(' · ') : null;
		}
		case 'DAILY_CO_FAILED': {
			const operation = readString(metadata.operation);
			const error = readString(metadata.error);
			if (operation && error) return `${operation}: ${error}`;
			return operation ?? error ?? null;
		}
		case 'DAILY_CO_TRANSCRIPTION_FAILED':
		case 'DAILY_CO_TRANSCRIPTION_START_FAILED':
		case 'CLAUDE_API_FAILED': {
			const error = readString(metadata.error);
			return error ? `erro: ${error}` : null;
		}
		case 'DAILY_CO_TRANSCRIPTION_SKIPPED':
		case 'CLAUDE_API_SKIPPED': {
			const reason = readString(metadata.reason);
			return reason ? `motivo: ${reason}` : null;
		}
		case 'CLAUDE_API_PROCESSED': {
			const inputTokens = readNumber(metadata.inputTokens);
			const outputTokens = readNumber(metadata.outputTokens);
			if (inputTokens != null && outputTokens != null) {
				return `tokens: ${inputTokens} in / ${outputTokens} out`;
			}
			return null;
		}
		case 'PROFESSIONAL_AUTHORIZED':
		case 'PROFESSIONAL_DEAUTHORIZED':
		case 'PROFESSIONAL_BANNED':
		case 'PROFESSIONAL_UNBANNED':
			return formatAdminIdSummary(metadata);
		case 'PROFESSIONAL_PERMISSION_TAGS_UPDATED': {
			const adminPart = formatAdminIdSummary(metadata);
			const tags = readStringArray(metadata.tags);
			const tagLabels = tags
				?.map((tag) =>
					isProfessionalPermissionTag(tag)
						? getProfessionalPermissionTagLabel(tag)
						: tag,
				)
				.join(', ');
			const parts = [
				adminPart,
				tagLabels ? `tags: ${tagLabels}` : null,
			].filter((part): part is string => part != null);
			return parts.length > 0 ? parts.join(' · ') : null;
		}
		case 'PROFESSIONAL_VISIBILITY_UPDATED': {
			const adminPart = formatAdminIdSummary(metadata);
			const visibility = readString(metadata.visibility);
			const visibilityLabel =
				visibility && isProfessionalVisibility(visibility)
					? PROFESSIONAL_VISIBILITY_LABELS[visibility]
					: visibility;
			const parts = [
				adminPart,
				visibilityLabel ? `visibilidade: ${visibilityLabel}` : null,
			].filter((part): part is string => part != null);
			return parts.length > 0 ? parts.join(' · ') : null;
		}
		case 'EMAIL_QUEUED': {
			const template = readString(metadata.template);
			return template ? `template: ${template}` : null;
		}
		default: {
			if (FILLED_FIELD_COUNT_ACTIONS.includes(action)) {
				return formatFilledFieldCountSummary(metadata);
			}
			return null;
		}
	}
};

const SCHEDULE_AUDIT_ACTIONS: AuditLogs.AuditLogAction[] = [
	'SCHEDULE_CREATED',
	'SCHEDULE_UPDATED',
	'SCHEDULE_DELETED',
	'SCHEDULE_BULK_DELETED',
];

const readWeekDayValue = (value: unknown): string | null => {
	if (typeof value === 'number' && Number.isFinite(value)) {
		return String(value);
	}
	if (typeof value === 'string' && value.trim()) {
		return value.trim();
	}
	return null;
};

export const resolveScheduleWeekDayLabel = (
	weekDay: unknown,
): string | null => {
	const raw = readWeekDayValue(weekDay);
	if (!raw) return null;

	const numeric = Number(raw);
	if (Number.isInteger(numeric) && numeric >= 0 && numeric <= 6) {
		return AUDIT_WEEKDAY_LABELS[numeric] ?? null;
	}

	const normalized = raw
		.normalize('NFD')
		.replace(/\p{M}/gu, '')
		.toUpperCase();

	const nameMap: Record<string, string> = {
		DOMINGO: 'domingo',
		DOM: 'domingo',
		SEGUNDA: 'segunda-feira',
		SEG: 'segunda-feira',
		TERCA: 'terça-feira',
		TER: 'terça-feira',
		QUARTA: 'quarta-feira',
		QUA: 'quarta-feira',
		QUINTA: 'quinta-feira',
		QUI: 'quinta-feira',
		SEXTA: 'sexta-feira',
		SEX: 'sexta-feira',
		SABADO: 'sábado',
		SAB: 'sábado',
	};

	return nameMap[normalized] ?? null;
};

const annotateScheduleWeekDayInMetadataJson = (
	json: string,
	weekDayLabel: string,
): string => {
	return json.replace(
		/("weekDay":\s*(?:"[^"]*"|\d+))(\s*,)?/,
		(_, valuePart: string, comma: string | undefined) =>
			`${valuePart}${comma ?? ''} // ${weekDayLabel}`,
	);
};

export const formatAuditMetadataBody = (
	metadata: unknown,
	action?: AuditLogs.AuditLogAction,
): string => {
	if (metadata == null) return '{}';

	try {
		const baseJson = JSON.stringify(metadata, null, 2);
		const isScheduleAction =
			action != null && SCHEDULE_AUDIT_ACTIONS.includes(action);

		if (
			!isScheduleAction ||
			!isRecord(metadata) ||
			metadata.weekDay == null
		) {
			return baseJson;
		}

		const weekDayLabel = resolveScheduleWeekDayLabel(metadata.weekDay);
		if (!weekDayLabel) {
			return baseJson;
		}

		return annotateScheduleWeekDayInMetadataJson(baseJson, weekDayLabel);
	} catch {
		return String(metadata);
	}
};

export const resolveAuditLogIcon = (
	category: AuditLogs.AuditLogCategory,
	action: AuditLogs.AuditLogAction,
): LucideIcon => {
	switch (action) {
		case 'LOGIN':
			return LogIn;
		case 'LOGIN_ATTEMPT':
			return ShieldAlert;
		case 'LOGOUT':
			return LogOut;
		case 'GOOGLE_OAUTH_AUTHORIZED':
			return Link2;
		case 'GOOGLE_OAUTH_REVOKED':
			return Unlink;
		case 'PROFILE_UPDATED':
			return UserPen;
		case 'SCHEDULE_CREATED':
			return CalendarPlus;
		case 'SCHEDULE_UPDATED':
			return CalendarClock;
		case 'SCHEDULE_DELETED':
		case 'SCHEDULE_BULK_DELETED':
			return CalendarX;
		case 'SESSION_SCHEDULED':
			return CalendarPlus;
		case 'SESSION_RESCHEDULED':
			return CalendarClock;
		case 'SESSION_STATUS_UPDATED':
			return RefreshCw;
		case 'SESSION_CANCELLED':
			return CalendarX;
		case 'SESSION_STARTED':
			return Play;
		case 'SESSION_FINISHED':
			return Video;
		case 'PATIENT_CREATED':
			return UserPlus;
		case 'PATIENT_UPDATED':
			return UserPen;
		case 'PATIENT_DELETED':
			return UserMinus;
		case 'MEDICAL_RECORD_CREATED':
		case 'MEDICAL_RECORD_UPDATED':
		case 'MEDICAL_RECORD_SESSION_CREATED':
		case 'MEDICAL_RECORD_SESSION_UPDATED':
			return FileText;
		case 'MEDICAL_RECORD_ASSESSMENT_CREATED':
		case 'MEDICAL_RECORD_ASSESSMENT_UPDATED':
		case 'MEDICAL_RECORD_ASSESSMENT_DELETED':
		case 'MEDICAL_RECORD_PLAN_CREATED':
		case 'MEDICAL_RECORD_PLAN_UPDATED':
		case 'MEDICAL_RECORD_TASK_CREATED':
		case 'MEDICAL_RECORD_TASK_UPDATED':
			return ClipboardList;
		case 'UNAVAILABILITY_CREATED':
			return CalendarOff;
		case 'UNAVAILABILITY_UPDATED':
			return CalendarClock;
		case 'UNAVAILABILITY_DELETED':
			return CalendarX;
		case 'DAILY_CO_CALL_STARTED':
		case 'DAILY_CO_ROOM_CREATED':
		case 'DAILY_CO_ROOM_RECONCILED':
			return Video;
		case 'DAILY_CO_TRANSCRIPTION_STARTED':
		case 'DAILY_CO_TRANSCRIPTION_FINISHED':
			return Mic;
		case 'DAILY_CO_TRANSCRIPTION_SKIPPED':
			return Mic;
		case 'DAILY_CO_FAILED':
		case 'DAILY_CO_TRANSCRIPTION_FAILED':
		case 'DAILY_CO_TRANSCRIPTION_START_FAILED':
		case 'CLAUDE_API_FAILED':
			return AlertTriangle;
		case 'CLAUDE_API_PROCESSED':
			return Sparkles;
		case 'CLAUDE_API_SKIPPED':
			return Sparkles;
		case 'PROFESSIONAL_AUTHORIZED':
			return ShieldCheck;
		case 'PROFESSIONAL_DEAUTHORIZED':
			return ShieldOff;
		case 'PROFESSIONAL_PERMISSION_TAGS_UPDATED':
			return KeyRound;
		case 'PROFESSIONAL_BANNED':
			return ShieldAlert;
		case 'PROFESSIONAL_UNBANNED':
			return ShieldCheck;
		case 'PROFESSIONAL_VISIBILITY_UPDATED':
			return Eye;
		case 'EMAIL_QUEUED':
			return Mail;
		default:
			break;
	}

	switch (category) {
		case 'AUTHENTICATION':
			return KeyRound;
		case 'PROFILE':
			return UserPen;
		case 'SCHEDULE':
			return Calendar;
		case 'SESSION':
			return Video;
		case 'EXTERNAL_API':
			return Bot;
		case 'AUTHORIZATION':
			return ShieldCheck;
		case 'NOTIFICATION':
			return Mail;
		case 'PATIENT':
			return Users;
		case 'MEDICAL_RECORD':
			return ClipboardList;
		case 'UNAVAILABILITY':
			return CalendarOff;
		default:
			return Calendar;
	}
};

export const dateInputToIsoStart = (value: string): string => {
	const [year, month, day] = value.split('-').map(Number);
	return new Date(year, month - 1, day, 0, 0, 0, 0).toISOString();
};

export const dateInputToIsoEnd = (value: string): string => {
	const [year, month, day] = value.split('-').map(Number);
	return new Date(year, month - 1, day, 23, 59, 59, 999).toISOString();
};
